import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';
import { Text } from '@/design-system/atoms/Text';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import { Video } from '@/design-system/atoms/Video';
import { VideoCard } from '@/design-system/molecules/VideoCard';
import { useBreakpoint } from '@/hooks/useBreakpoint';

import styles from './PhysicianAboutMe.module.scss';

const PhysicianAboutMe = ({
    title = 'About Me',
    video,
    aboutMe,
    areasOfInterestTitle = 'Professional Interests: ',
    areasOfInterest,
    specialtiesTitle = 'Specialties',
    primarySpecialty,
    specialties,
    affiliationsTitle = 'Affiliations',
    affiliations,
    props,
}) => {
    const MOBILE_HEIGHT = 5;
    const DESKTOP_HEIGHT = 7;

    const aboutMeRef = useRef();

    const [isExpandable, setIsExpandable] = React.useState(false);
    const [maxHeight, setMaxHeight] = React.useState();
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [aboutMeHeight, setAboutMeHeight] = React.useState();
    const [aboutChild, setAboutChild] = React.useState();
    const [aboutMeLineHeight, setAboutMeLineHeight] = React.useState();

    const breakpoint = useBreakpoint();
    const isMobile = !breakpoint?.large;

    const FormatMaxHeight = useCallback((height, multiplier = 1) => {
        return `${height * multiplier}px`;
    }, []);

    const filteredSpecialites = useMemo(() => {
        let filtered = specialties;
        if (Array.isArray(specialties) && primarySpecialty) {
            filtered = specialties.filter((specialty) => specialty !== primarySpecialty);
        }
        return filtered;
    }, [specialties, primarySpecialty]);

    const HandleToggle = () => {
        setIsExpanded(!isExpanded);

        if (isExpanded && isMobile) {
            setMaxHeight(FormatMaxHeight(aboutMeLineHeight, DESKTOP_HEIGHT));
        } else if (isExpanded && !isMobile) {
            setMaxHeight(FormatMaxHeight(aboutMeLineHeight, MOBILE_HEIGHT));
        } else {
            setMaxHeight(FormatMaxHeight(aboutMeHeight));
        }
    };

    useEffect(() => {
        if (!aboutMe || !aboutMeRef.current) return;

        const recalcHeight = () => {
            let totalHeight = 0;
            const children = aboutMeRef.current.children;
            for (let i = 0; i < children.length; i++) {
                totalHeight += children[i].offsetHeight;
            }
            setAboutMeHeight(totalHeight);
            setAboutChild(children[0]);

            if (isExpanded) {
                setMaxHeight(FormatMaxHeight(totalHeight));
            }
        };

        recalcHeight();
        window.addEventListener('resize', recalcHeight);
        return () => {
            window.removeEventListener('resize', recalcHeight);
        };
    }, [aboutMe, aboutMeRef, isExpanded, FormatMaxHeight]);

    useEffect(() => {
        if (!aboutMe) return;

        if (aboutChild) {
            setAboutMeLineHeight(parseFloat(window.getComputedStyle(aboutChild).lineHeight));
        }

        if (!aboutMeRef.current) return;

        if (isMobile) {
            setIsExpandable(aboutMeHeight > aboutMeLineHeight * DESKTOP_HEIGHT);
            if (isExpandable) {
                setMaxHeight(FormatMaxHeight(aboutMeLineHeight, DESKTOP_HEIGHT));
            }
        } else {
            setIsExpandable(aboutMeHeight > aboutMeLineHeight * MOBILE_HEIGHT);
            if (isExpandable) {
                setMaxHeight(FormatMaxHeight(aboutMeLineHeight, MOBILE_HEIGHT));
            }
        }
    }, [
        isMobile,
        isExpandable,
        aboutMeHeight,
        aboutMeLineHeight,
        aboutChild,
        aboutMe,
        FormatMaxHeight,
    ]);
    return (
        <>
            {!video && !aboutMe && !areasOfInterest ? (
                <ContentContainer {...props} id="about" className={styles[`physician-about-me`]}>
                    <Title
                        variant={Title.VARIANT.H3}
                        tag={Title.TAG.H2}
                        content={title}
                        className={styles[`physician-about-me__title`]}
                    />
                    <Theme
                        name={Theme.NAME.TREATMENTS_AND_SERVICES}
                        className={styles[`physician-about-me__list-wrapper`]}
                    >
                        <div className={styles[`physician-about-me__container`]}>
                            {(primarySpecialty ||
                                (Array.isArray(filteredSpecialites) &&
                                    filteredSpecialites.length > 0)) && (
                                <div className={styles[`physician-about-me__list`]}>
                                    <Text
                                        variant={Text.VARIANT.E1}
                                        tag={Text.TAG.P}
                                        content={specialtiesTitle}
                                        className={styles[`physician-about-me__list-title`]}
                                    />
                                    <ul>
                                        {primarySpecialty && (
                                            <li className={styles[`physician-about-me__list-item`]}>
                                                <Text
                                                    variant={Text.VARIANT.T2}
                                                    content={primarySpecialty}
                                                />
                                            </li>
                                        )}

                                        {Array.isArray(filteredSpecialites) &&
                                            filteredSpecialites.length > 0 && (
                                                <>
                                                    {filteredSpecialites.map((specialty) => (
                                                        <li
                                                            key={specialty}
                                                            className={
                                                                styles[
                                                                    `physician-about-me__list-item`
                                                                ]
                                                            }
                                                        >
                                                            <Text
                                                                variant={Text.VARIANT.T2}
                                                                content={specialty}
                                                            />
                                                        </li>
                                                    ))}
                                                </>
                                            )}
                                    </ul>
                                </div>
                            )}

                            {Array.isArray(affiliations) && affiliations.length > 0 && (
                                <div className={styles[`physician-about-me__list`]}>
                                    <Text
                                        variant={Text.VARIANT.E1}
                                        tag={Text.TAG.P}
                                        content={affiliationsTitle}
                                        className={styles[`physician-about-me__list-title`]}
                                    />
                                    <ul className="GlobalLinkNoTx">
                                        {affiliations.map((affiliation) => (
                                            <li
                                                key={affiliation}
                                                className={styles[`physician-about-me__list-item`]}
                                            >
                                                <Text
                                                    variant={Text.VARIANT.T2}
                                                    content={affiliation}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </Theme>
                </ContentContainer>
            ) : (
                <ContentContainer id="about" {...props} className={styles[`physician-about-me`]}>
                    <Grid col={{ sm: 4, md: 6, lg: 12 }}>
                        <GridColumn colSpan={{ sm: 4, md: 6, lg: 8 }}>
                            <Title
                                variant={Title.VARIANT.H3}
                                tag={Title.TAG.H2}
                                content={title}
                                className={styles[`physician-about-me__title`]}
                            />
                            {video && (
                                <VideoCard
                                    {...video}
                                    title={null}
                                    body={null}
                                    contentWidth="fullWidth"
                                    inGrid
                                />
                            )}
                            {aboutMe && (
                                <div className={styles[`physician-about-me__about-container`]}>
                                    <div
                                        ref={aboutMeRef}
                                        className={styles[`physician-about-me__about-inner`]}
                                        style={{
                                            height: isExpandable ? maxHeight : 'unset',
                                        }}
                                    >
                                        <RichTextEditor
                                            text={aboutMe}
                                            className={styles[`physician-about-me__about-text`]}
                                        />
                                    </div>

                                    {isExpandable && (
                                        <Button
                                            buttonStyle={Button.STYLE.TEXT_EXTRA_SMALL}
                                            label={isExpanded ? 'Show Less' : 'Show More'}
                                            onClick={HandleToggle}
                                            className={styles[`physician-about-me__button`]}
                                            analytics={{
                                                componentName: 'fad_bio_about_me',
                                                componentTitle: title,
                                                interactionType: 'fad_bio_cta',
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                            {areasOfInterest && (
                                <div className={styles[`physician-about-me__interest`]}>
                                    <Text
                                        variant={Text.VARIANT.T2B}
                                        tag={Text.TAG.SPAN}
                                        content={`${areasOfInterestTitle} `}
                                    />
                                    <Text
                                        variant={Text.VARIANT.T2}
                                        tag={Text.TAG.SPAN}
                                        content={areasOfInterest.join(', ')}
                                    />
                                </div>
                            )}
                        </GridColumn>
                        <GridColumn colSpan={{ sm: 4, md: 6, lg: 4 }}>
                            <Theme
                                name={Theme.NAME.TREATMENTS_AND_SERVICES}
                                className={styles[`physician-about-me__list-wrapper`]}
                            >
                                <div className={styles[`physician-about-me__container`]}>
                                    {(primarySpecialty ||
                                        (Array.isArray(filteredSpecialites) &&
                                            filteredSpecialites.length > 1)) && (
                                        <div className={styles[`physician-about-me__list`]}>
                                            <Text
                                                variant={Text.VARIANT.E1}
                                                tag={Text.TAG.P}
                                                content={specialtiesTitle}
                                                className={styles[`physician-about-me__list-title`]}
                                            />
                                            <ul>
                                                {primarySpecialty && (
                                                    <li
                                                        className={
                                                            styles[`physician-about-me__list-item`]
                                                        }
                                                    >
                                                        <Text
                                                            variant={Text.VARIANT.T2}
                                                            content={primarySpecialty}
                                                        />
                                                    </li>
                                                )}

                                                {Array.isArray(filteredSpecialites) &&
                                                    filteredSpecialites.length > 0 && (
                                                        <>
                                                            {filteredSpecialites.map(
                                                                (specialty) => (
                                                                    <li
                                                                        key={specialty}
                                                                        className={
                                                                            styles[
                                                                                `physician-about-me__list-item`
                                                                            ]
                                                                        }
                                                                    >
                                                                        <Text
                                                                            variant={
                                                                                Text.VARIANT.T2
                                                                            }
                                                                            content={specialty}
                                                                        />
                                                                    </li>
                                                                ),
                                                            )}
                                                        </>
                                                    )}
                                            </ul>
                                        </div>
                                    )}

                                    {Array.isArray(affiliations) && affiliations.length > 0 && (
                                        <div className={styles[`physician-about-me__list`]}>
                                            <Text
                                                variant={Text.VARIANT.E1}
                                                tag={Text.TAG.P}
                                                content={affiliationsTitle}
                                                className={styles[`physician-about-me__list-title`]}
                                            />
                                            <ul className="GlobalLinkNoTx">
                                                {affiliations.map((affiliation) => (
                                                    <li
                                                        key={affiliation}
                                                        className={
                                                            styles[`physician-about-me__list-item`]
                                                        }
                                                    >
                                                        <Text
                                                            variant={Text.VARIANT.T2}
                                                            content={affiliation}
                                                        />
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </Theme>
                        </GridColumn>
                    </Grid>
                </ContentContainer>
            )}
        </>
    );
};

PhysicianAboutMe.propTypes = {
    /**
     * Title of the section
     */
    title: PropTypes.string,
    /**
     * Video object
     */
    video: Video.propTypes,
    /**
     * About me text
     */
    aboutMe: PropTypes.string,
    /**
     * Areas of interest
     */
    areasOfInterest: PropTypes.arrayOf(PropTypes.string),
    /**
     * Primary specialty
     */
    primarySpecialty: PropTypes.string,
    /**
     * Array of specialties
     */
    specialties: PropTypes.arrayOf(PropTypes.string),
    /**
     * Array of affiliations
     */
    affiliations: PropTypes.arrayOf(PropTypes.string),
};

export default PhysicianAboutMe;
